import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { InvalidationType } from '../enums/invalidation-type';
import { StatusCode } from '../enums/status-code';
import { ProxyResourceType } from '../enums/proxy-resource-type';
export class Proxy {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Create a new CDN cache invalidation for a domain. Executes a hard purge of cached content.
     *
     * Depending on type, the invalidation purges a single cache tag, a single URL path, or all cached content for the domain.
     *
     * Domains that route to a function do not support cache invalidation.
     *
     * @param {string} params.domain - Domain name.
     * @param {InvalidationType} params.type - Type of reference passed. Allowed values are: tag, path, all
     * @param {string} params.reference - Reference to invalidate. Depending on type this can be: cache tag name (up to 128 characters), URL path (up to 2048 characters). Not required when type is all.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyInvalidation>}
     */
    createInvalidation(params: {
        domain: string;
        type: InvalidationType;
        reference?: string;
    }): Promise<Models.ProxyInvalidation>;
    /**
     * Create a new CDN cache invalidation for a domain. Executes a hard purge of cached content.
     *
     * Depending on type, the invalidation purges a single cache tag, a single URL path, or all cached content for the domain.
     *
     * Domains that route to a function do not support cache invalidation.
     *
     * @param {string} domain - Domain name.
     * @param {InvalidationType} type - Type of reference passed. Allowed values are: tag, path, all
     * @param {string} reference - Reference to invalidate. Depending on type this can be: cache tag name (up to 128 characters), URL path (up to 2048 characters). Not required when type is all.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyInvalidation>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createInvalidation(
        domain: string,
        type: InvalidationType,
        reference?: string,
    ): Promise<Models.ProxyInvalidation>;
    createInvalidation(
        paramsOrFirst:
            | { domain: string; type: InvalidationType; reference?: string }
            | string,
        ...rest: [InvalidationType?, string?]
    ): Promise<Models.ProxyInvalidation> {
        let params: {
            domain: string;
            type: InvalidationType;
            reference?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                type: InvalidationType;
                reference?: string;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                type: rest[0] as InvalidationType,
                reference: rest[1] as string,
            };
        }

        const domain = params.domain;
        const type = params.type;
        const reference = params.reference;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/proxy/invalidations';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof type !== 'undefined') {
            apiPayload['type'] = type;
        }
        if (typeof reference !== 'undefined') {
            apiPayload['reference'] = reference;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyInvalidation',
            models: {
                proxyInvalidation: {
                    defaults: {
                        domain: '',
                        type: '',
                        reference: '',
                        status: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all the proxy rules. You can use the query params to filter your results.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: domain, type, trigger, deploymentResourceType, deploymentResourceId, deploymentId, deploymentVcsProviderBranch, protocol
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRuleList>}
     */
    listRules(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.ProxyRuleList>;
    /**
     * Get a list of all the proxy rules. You can use the query params to filter your results.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: domain, type, trigger, deploymentResourceType, deploymentResourceId, deploymentId, deploymentVcsProviderBranch, protocol
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRuleList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listRules(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.ProxyRuleList>;
    listRules(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.ProxyRuleList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/proxy/rules';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRuleList',
            models: {
                proxyRuleList: {
                    defaults: {
                        total: 0,
                        rules: [],
                    },
                    properties: {
                        rules: {
                            items: 'proxyRule',
                        },
                    },
                },
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new proxy rule for serving Appwrite's API on custom domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} params.domain - Domain name.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    createAPIRule(params: { domain: string }): Promise<Models.ProxyRule>;
    /**
     * Create a new proxy rule for serving Appwrite's API on custom domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} domain - Domain name.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createAPIRule(domain: string): Promise<Models.ProxyRule>;
    createAPIRule(
        paramsOrFirst: { domain: string } | string,
    ): Promise<Models.ProxyRule> {
        let params: { domain: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { domain: string };
        } else {
            params = {
                domain: paramsOrFirst as string,
            };
        }

        const domain = params.domain;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        const apiPath = '/proxy/rules/api';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new proxy rule for executing Appwrite Function on custom domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} params.domain - Domain name.
     * @param {string} params.functionId - ID of function to be executed.
     * @param {string} params.branch - Name of VCS branch to deploy changes automatically
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    createFunctionRule(params: {
        domain: string;
        functionId: string;
        branch?: string;
    }): Promise<Models.ProxyRule>;
    /**
     * Create a new proxy rule for executing Appwrite Function on custom domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} domain - Domain name.
     * @param {string} functionId - ID of function to be executed.
     * @param {string} branch - Name of VCS branch to deploy changes automatically
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createFunctionRule(
        domain: string,
        functionId: string,
        branch?: string,
    ): Promise<Models.ProxyRule>;
    createFunctionRule(
        paramsOrFirst:
            { domain: string; functionId: string; branch?: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.ProxyRule> {
        let params: { domain: string; functionId: string; branch?: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                functionId: string;
                branch?: string;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                functionId: rest[0] as string,
                branch: rest[1] as string,
            };
        }

        const domain = params.domain;
        const functionId = params.functionId;
        const branch = params.branch;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof functionId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "functionId"',
            );
        }
        const apiPath = '/proxy/rules/function';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof functionId !== 'undefined') {
            apiPayload['functionId'] = functionId;
        }
        if (typeof branch !== 'undefined') {
            apiPayload['branch'] = branch;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new proxy rule for to redirect from custom domain to another domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} params.domain - Domain name.
     * @param {string} params.url - Target URL of redirection
     * @param {StatusCode} params.statusCode - Status code of redirection
     * @param {string} params.resourceId - ID of parent resource.
     * @param {ProxyResourceType} params.resourceType - Type of parent resource.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    createRedirectRule(params: {
        domain: string;
        url: string;
        statusCode: StatusCode;
        resourceId: string;
        resourceType: ProxyResourceType;
    }): Promise<Models.ProxyRule>;
    /**
     * Create a new proxy rule for to redirect from custom domain to another domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} domain - Domain name.
     * @param {string} url - Target URL of redirection
     * @param {StatusCode} statusCode - Status code of redirection
     * @param {string} resourceId - ID of parent resource.
     * @param {ProxyResourceType} resourceType - Type of parent resource.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRedirectRule(
        domain: string,
        url: string,
        statusCode: StatusCode,
        resourceId: string,
        resourceType: ProxyResourceType,
    ): Promise<Models.ProxyRule>;
    createRedirectRule(
        paramsOrFirst:
            | {
                  domain: string;
                  url: string;
                  statusCode: StatusCode;
                  resourceId: string;
                  resourceType: ProxyResourceType;
              }
            | string,
        ...rest: [string?, StatusCode?, string?, ProxyResourceType?]
    ): Promise<Models.ProxyRule> {
        let params: {
            domain: string;
            url: string;
            statusCode: StatusCode;
            resourceId: string;
            resourceType: ProxyResourceType;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                url: string;
                statusCode: StatusCode;
                resourceId: string;
                resourceType: ProxyResourceType;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                url: rest[0] as string,
                statusCode: rest[1] as StatusCode,
                resourceId: rest[2] as string,
                resourceType: rest[3] as ProxyResourceType,
            };
        }

        const domain = params.domain;
        const url = params.url;
        const statusCode = params.statusCode;
        const resourceId = params.resourceId;
        const resourceType = params.resourceType;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof url === 'undefined') {
            throw new AppwriteException('Missing required parameter: "url"');
        }
        if (typeof statusCode === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "statusCode"',
            );
        }
        if (typeof resourceId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resourceId"',
            );
        }
        if (typeof resourceType === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "resourceType"',
            );
        }
        const apiPath = '/proxy/rules/redirect';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof url !== 'undefined') {
            apiPayload['url'] = url;
        }
        if (typeof statusCode !== 'undefined') {
            apiPayload['statusCode'] = statusCode;
        }
        if (typeof resourceId !== 'undefined') {
            apiPayload['resourceId'] = resourceId;
        }
        if (typeof resourceType !== 'undefined') {
            apiPayload['resourceType'] = resourceType;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new proxy rule for serving Appwrite Site on custom domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} params.domain - Domain name.
     * @param {string} params.siteId - ID of site to be executed.
     * @param {string} params.branch - Name of VCS branch to deploy changes automatically
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    createSiteRule(params: {
        domain: string;
        siteId: string;
        branch?: string;
    }): Promise<Models.ProxyRule>;
    /**
     * Create a new proxy rule for serving Appwrite Site on custom domain.
     *
     * Rule ID is automatically generated as MD5 hash of a rule domain for performance purposes.
     *
     * @param {string} domain - Domain name.
     * @param {string} siteId - ID of site to be executed.
     * @param {string} branch - Name of VCS branch to deploy changes automatically
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSiteRule(
        domain: string,
        siteId: string,
        branch?: string,
    ): Promise<Models.ProxyRule>;
    createSiteRule(
        paramsOrFirst:
            { domain: string; siteId: string; branch?: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.ProxyRule> {
        let params: { domain: string; siteId: string; branch?: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                siteId: string;
                branch?: string;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                siteId: rest[0] as string,
                branch: rest[1] as string,
            };
        }

        const domain = params.domain;
        const siteId = params.siteId;
        const branch = params.branch;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof siteId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "siteId"');
        }
        const apiPath = '/proxy/rules/site';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof siteId !== 'undefined') {
            apiPayload['siteId'] = siteId;
        }
        if (typeof branch !== 'undefined') {
            apiPayload['branch'] = branch;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create an SMTP ingress rule that asynchronously executes an Appwrite Function for incoming email.
     *
     * @param {string} params.domain - Domain that receives email.
     * @param {string} params.functionId - ID of function to execute.
     * @param {string} params.branch - Name of VCS branch to deploy changes automatically.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    createSMTPRule(params: {
        domain: string;
        functionId: string;
        branch?: string;
    }): Promise<Models.ProxyRule>;
    /**
     * Create an SMTP ingress rule that asynchronously executes an Appwrite Function for incoming email.
     *
     * @param {string} domain - Domain that receives email.
     * @param {string} functionId - ID of function to execute.
     * @param {string} branch - Name of VCS branch to deploy changes automatically.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSMTPRule(
        domain: string,
        functionId: string,
        branch?: string,
    ): Promise<Models.ProxyRule>;
    createSMTPRule(
        paramsOrFirst:
            { domain: string; functionId: string; branch?: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.ProxyRule> {
        let params: { domain: string; functionId: string; branch?: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                domain: string;
                functionId: string;
                branch?: string;
            };
        } else {
            params = {
                domain: paramsOrFirst as string,
                functionId: rest[0] as string,
                branch: rest[1] as string,
            };
        }

        const domain = params.domain;
        const functionId = params.functionId;
        const branch = params.branch;

        if (typeof domain === 'undefined') {
            throw new AppwriteException('Missing required parameter: "domain"');
        }
        if (typeof functionId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "functionId"',
            );
        }
        const apiPath = '/proxy/rules/smtp';
        const apiPayload: Payload = {};
        if (typeof domain !== 'undefined') {
            apiPayload['domain'] = domain;
        }
        if (typeof functionId !== 'undefined') {
            apiPayload['functionId'] = functionId;
        }
        if (typeof branch !== 'undefined') {
            apiPayload['branch'] = branch;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a proxy rule by its unique ID.
     *
     * @param {string} params.ruleId - Rule ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    getRule(params: { ruleId: string }): Promise<Models.ProxyRule>;
    /**
     * Get a proxy rule by its unique ID.
     *
     * @param {string} ruleId - Rule ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getRule(ruleId: string): Promise<Models.ProxyRule>;
    getRule(
        paramsOrFirst: { ruleId: string } | string,
    ): Promise<Models.ProxyRule> {
        let params: { ruleId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { ruleId: string };
        } else {
            params = {
                ruleId: paramsOrFirst as string,
            };
        }

        const ruleId = params.ruleId;

        if (typeof ruleId === 'undefined' || ruleId === '') {
            throw new AppwriteException('Missing required parameter: "ruleId"');
        }
        const apiPath = '/proxy/rules/{ruleId}'.replace(
            '{ruleId}',
            encodeURIComponent(String(ruleId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a proxy rule by its unique ID.
     *
     * @param {string} params.ruleId - Rule ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteRule(params: { ruleId: string }): Promise<{}>;
    /**
     * Delete a proxy rule by its unique ID.
     *
     * @param {string} ruleId - Rule ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteRule(ruleId: string): Promise<{}>;
    deleteRule(paramsOrFirst: { ruleId: string } | string): Promise<{}> {
        let params: { ruleId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { ruleId: string };
        } else {
            params = {
                ruleId: paramsOrFirst as string,
            };
        }

        const ruleId = params.ruleId;

        if (typeof ruleId === 'undefined' || ruleId === '') {
            throw new AppwriteException('Missing required parameter: "ruleId"');
        }
        const apiPath = '/proxy/rules/{ruleId}'.replace(
            '{ruleId}',
            encodeURIComponent(String(ruleId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * If not succeeded yet, retry verification process of a proxy rule domain. This endpoint triggers domain verification by checking DNS records. If verification is successful, a TLS certificate will be automatically provisioned for the domain asynchronously in the background.
     *
     * @param {string} params.ruleId - Rule ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     */
    updateRuleStatus(params: { ruleId: string }): Promise<Models.ProxyRule>;
    /**
     * If not succeeded yet, retry verification process of a proxy rule domain. This endpoint triggers domain verification by checking DNS records. If verification is successful, a TLS certificate will be automatically provisioned for the domain asynchronously in the background.
     *
     * @param {string} ruleId - Rule ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.ProxyRule>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRuleStatus(ruleId: string): Promise<Models.ProxyRule>;
    updateRuleStatus(
        paramsOrFirst: { ruleId: string } | string,
    ): Promise<Models.ProxyRule> {
        let params: { ruleId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { ruleId: string };
        } else {
            params = {
                ruleId: paramsOrFirst as string,
            };
        }

        const ruleId = params.ruleId;

        if (typeof ruleId === 'undefined' || ruleId === '') {
            throw new AppwriteException('Missing required parameter: "ruleId"');
        }
        const apiPath = '/proxy/rules/{ruleId}/status'.replace(
            '{ruleId}',
            encodeURIComponent(String(ruleId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'proxyRule',
            models: {
                proxyRule: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        domain: '',
                        type: '',
                        trigger: '',
                        redirectUrl: '',
                        redirectStatusCode: 301,
                        deploymentId: '',
                        deploymentResourceId: '',
                        deploymentVcsProviderBranch: '',
                        status: 'unverified',
                        logs: '',
                        protocol: 'http',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
